import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, useCallback } from 'react'
import { spiritualPractices, inspirationalQuotes } from '@/data/frameworks'
import { cn } from '@/lib/utils'
import {
  Sparkles,
  Heart,
  Sun,
  Moon,
  Star,
  Compass,
  BookOpen,
  Users,
  Check,
  CheckCircle,
  XCircle,
  Award,
  Trophy,
  Share2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Lightbulb,
  Brain,
  Globe,
  BookHeart,
  Scroll,
  Flower2
} from 'lucide-react'

const journeyStages = [
  {
    icon: Sun,
    title: 'Awakening',
    description: 'The first stirrings of spiritual awareness',
    practices: ['Meditation', 'Self-reflection', 'Reading sacred texts']
  },
  {
    icon: Compass,
    title: 'Seeking',
    description: 'Exploring different paths and teachings',
    practices: ['Study multiple traditions', 'Find a teacher', 'Ask deep questions']
  },
  {
    icon: Heart,
    title: 'Devotion',
    description: 'Deepening connection through practice',
    practices: ['Daily prayer', 'Service to others', 'Cultivating gratitude']
  },
  {
    icon: Star,
    title: 'Illumination',
    description: 'Moments of profound insight and clarity',
    practices: ['Contemplative practice', 'Retreats', 'Silent reflection']
  },
  {
    icon: Moon,
    title: 'Integration',
    description: 'Living wisdom in everyday life',
    practices: ['Mindful living', 'Ethical action', 'Teaching others']
  }
]

// Interactive Quiz Questions about Sacred Wisdom
const quizQuestions = [
  {
    id: 1,
    question: 'What is the common thread found across virtually all major world faith traditions?',
    options: [
      'Strict dietary laws',
      'The Golden Rule - treating others as you wish to be treated',
      'Mandatory pilgrimage requirements',
      'Specific prayer postures'
    ],
    correctIndex: 1,
    explanation: 'The Golden Rule appears in Christianity, Islam, Judaism, Hinduism, Buddhism, Confucianism, and many other traditions, making it perhaps the most universal ethical principle across human spirituality.'
  },
  {
    id: 2,
    question: 'Which spiritual practice is found in ALL major world religions?',
    options: [
      'Animal sacrifice',
      'Meditation or contemplative prayer',
      'Fasting during winter months',
      'River baptism'
    ],
    correctIndex: 1,
    explanation: 'Every major world religion includes some form of meditation, contemplation, or prayerful silence - a practice of turning inward to connect with the divine or achieve spiritual insight.'
  },
  {
    id: 3,
    question: 'What does "interfaith dialogue" aim to achieve?',
    options: [
      'Converting others to your religion',
      'Proving one tradition is superior',
      'Finding common ground while respecting differences',
      'Creating a single unified religion'
    ],
    correctIndex: 2,
    explanation: 'Interfaith dialogue seeks mutual understanding and respect between different religious traditions, celebrating both shared values and unique insights without diminishing any tradition.'
  },
  {
    id: 4,
    question: 'The concept of "sacred texts" in world religions typically refers to:',
    options: [
      'Only ancient writings over 2000 years old',
      'Writings believed to contain divine wisdom or revelation',
      'Any books written by religious leaders',
      'Only texts written in Hebrew or Sanskrit'
    ],
    correctIndex: 1,
    explanation: 'Sacred texts are scriptures that a faith community believes contain divine truth, wisdom, or revelation, regardless of their age or original language.'
  },
  {
    id: 5,
    question: 'What is the primary purpose of spiritual practices across traditions?',
    options: [
      'To earn rewards in the afterlife',
      'To demonstrate superiority over others',
      'To transform consciousness and deepen connection with the sacred',
      'To follow rules without understanding'
    ],
    correctIndex: 2,
    explanation: 'While specific goals vary, spiritual practices universally aim at transformation - deepening awareness, cultivating virtues, and fostering connection with what each tradition considers sacred or ultimate.'
  }
]

// Practice Scenarios for Sacred Wisdom Application
const practiceScenarios = [
  {
    id: 1,
    title: 'Interfaith Conversation',
    description: 'A colleague asks why you study multiple religious traditions when you have your own faith. How do you respond?',
    options: [
      {
        text: 'Explain that studying other traditions deepens understanding of your own while revealing universal truths',
        feedback: 'Excellent response! This honors both your own tradition and others, showing how interfaith study enriches rather than diminishes faith.'
      },
      {
        text: 'Say that all religions are basically the same so it does not matter which you study',
        feedback: 'This overlooks the important differences between traditions. Each faith has unique insights worth respecting.'
      },
      {
        text: 'Avoid the question by changing the subject',
        feedback: 'This misses an opportunity for meaningful dialogue. Honest engagement builds understanding and trust.'
      }
    ]
  },
  {
    id: 2,
    title: 'Sacred Text Interpretation',
    description: 'You encounter a passage in a sacred text that seems to contradict modern ethical values. How do you approach this?',
    options: [
      {
        text: 'Reject the entire text as outdated and irrelevant',
        feedback: 'This dismissive approach loses the wisdom the text may still offer and ignores historical context.'
      },
      {
        text: 'Study the historical context, seek multiple interpretations, and discern timeless principles from cultural context',
        feedback: 'Excellent approach! Mature scriptural reading distinguishes eternal wisdom from cultural context while remaining open to deeper meanings.'
      },
      {
        text: 'Accept everything literally without question',
        feedback: 'Uncritical reading can miss the deeper spiritual meanings and lead to misapplication of ancient wisdom.'
      }
    ]
  },
  {
    id: 3,
    title: 'Spiritual Practice Integration',
    description: 'You want to start a daily spiritual practice but struggle with consistency. What is the wisest approach?',
    options: [
      {
        text: 'Commit to a 2-hour practice immediately to show dedication',
        feedback: 'Starting too ambitiously often leads to burnout. Sustainable practice builds gradually.'
      },
      {
        text: 'Wait until you feel inspired before practicing',
        feedback: 'Waiting for inspiration often means never beginning. Discipline creates the conditions for inspiration.'
      },
      {
        text: 'Begin with a small, consistent practice (5-10 minutes) at a set time, gradually expanding as it becomes natural',
        feedback: 'Perfect wisdom! The great teachers all emphasize starting small and building consistency. A brief daily practice surpasses sporadic lengthy sessions.'
      }
    ]
  }
]

// Resource Library Topics
const resourceLibrary = [
  {
    id: 'sacred-texts',
    title: 'Sacred Texts Overview',
    icon: Scroll,
    description: 'Introductions to major world scriptures and their teachings',
    topics: [
      'The Bible: Structure and themes across Old and New Testaments',
      'The Quran: Revelation, structure, and core teachings',
      'Torah and Talmud: Written and oral tradition in Judaism',
      'Bhagavad Gita: Hindu philosophy in poetic form',
      'Buddhist Sutras: Teachings of awakening',
      'Guru Granth Sahib: Living scripture of Sikhism'
    ]
  },
  {
    id: 'spiritual-practices',
    title: 'Contemplative Practices',
    icon: Flower2,
    description: 'Meditation, prayer, and spiritual disciplines across traditions',
    topics: [
      'Centering Prayer in Christian mysticism',
      'Dhikr: Remembrance practice in Sufism',
      'Jewish meditation and contemplative Kabbalah',
      'Buddhist mindfulness and insight meditation',
      'Hindu japa and mantra practice',
      'Indigenous sacred ceremonies and vision quests'
    ]
  },
  {
    id: 'comparative-themes',
    title: 'Universal Themes',
    icon: Globe,
    description: 'Common threads weaving through world faith traditions',
    topics: [
      'Divine Love across Christianity, Islam, Hinduism, and Judaism',
      'Compassion in Buddhism, Christianity, and Islam',
      'Sacred service and karma yoga',
      'Mystical union and enlightenment experiences',
      'Ethical principles and the Golden Rule',
      'Afterlife beliefs and ultimate purpose'
    ]
  },
  {
    id: 'interfaith-dialogue',
    title: 'Interfaith Understanding',
    icon: Users,
    description: 'Building bridges between religious traditions',
    topics: [
      'Principles of respectful religious dialogue',
      'Historical interfaith encounters and movements',
      'Contemporary interfaith organizations',
      'Navigating religious diversity in pluralistic society',
      'Interfaith families and celebrations',
      'Common misconceptions between traditions'
    ]
  },
  {
    id: 'mystical-traditions',
    title: 'Mystical Paths',
    icon: Sparkles,
    description: 'The esoteric and contemplative streams within religions',
    topics: [
      'Christian mysticism: Desert Fathers to modern contemplatives',
      'Sufism: The inner dimension of Islam',
      'Kabbalah: Jewish mystical tradition',
      'Hindu Vedanta and paths to liberation',
      'Zen Buddhism and direct experience',
      'Indigenous shamanic traditions'
    ]
  },
  {
    id: 'wisdom-teachers',
    title: 'Great Teachers',
    icon: BookHeart,
    description: 'Lives and teachings of spiritual luminaries',
    topics: [
      'Jesus, Muhammad, Moses, Buddha, Krishna',
      'Medieval mystics: Rumi, Hildegard, Meister Eckhart',
      'Modern teachers: Gandhi, Merton, Thich Nhat Hanh',
      'Contemporary voices in interfaith wisdom',
      'Women spiritual teachers across traditions',
      'The role of the guru, rabbi, imam, and priest'
    ]
  }
]

// Related Topics from the Knowledge Network
const relatedTopics = [
  {
    title: 'Global Knowledge Graph',
    description: 'Explore how sacred wisdom connects to broader human knowledge',
    url: 'https://globalknowledgegraph.com',
    icon: Globe
  },
  {
    title: 'Eternal Graph',
    description: 'Discover timeless wisdom patterns across cultures and ages',
    url: 'https://eternalgraph.com',
    icon: Star
  },
  {
    title: 'Truth Graph',
    description: 'Examine truth claims and reasoning in religious philosophy',
    url: 'https://truthgraph.com',
    icon: Brain
  },
  {
    title: 'Torah Verse',
    description: 'Deep dive into Jewish sacred texts and interpretation',
    url: 'https://torahverse.com',
    icon: Scroll
  }
]

// Knowledge Map Data for card-based display
const knowledgeCategories = [
  {
    id: 'wisdom',
    label: 'Sacred Wisdom',
    description: 'The central source of divine teachings across all faith traditions',
    color: '#D97706',
    items: ['Faith Traditions', 'Spiritual Practices', 'Sacred Texts', 'Universal Themes']
  },
  {
    id: 'traditions',
    label: 'Faith Traditions',
    description: 'The diverse paths humanity has walked toward the sacred',
    color: '#92400E',
    items: ['Christianity', 'Islam', 'Judaism', 'Hinduism', 'Buddhism', 'Sikhism']
  },
  {
    id: 'practices',
    label: 'Spiritual Practices',
    description: 'Methods of spiritual cultivation found across traditions',
    color: '#F59E0B',
    items: ['Meditation', 'Prayer', 'Sacred Service']
  },
  {
    id: 'themes',
    label: 'Universal Themes',
    description: 'Principles that transcend individual traditions',
    color: '#FCD34D',
    items: ['Divine Love', 'Compassion', 'Unity']
  }
]

export default function Step6() {
  const [selectedPractice, setSelectedPractice] = useState<string | null>(null)
  const [journeyProgress, setJourneyProgress] = useState<number[]>([])

  // Quiz State
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null)
  const [showExplanation, setShowExplanation] = useState(false)
  const [quizCompleted, setQuizCompleted] = useState(false)
  const [correctAnswers, setCorrectAnswers] = useState(0)
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<number>>(new Set())

  // Scenario State
  const [activeScenario, setActiveScenario] = useState<number | null>(null)
  const [scenarioAnswers, setScenarioAnswers] = useState<Record<number, number>>({})
  const [completedScenarios, setCompletedScenarios] = useState<Set<number>>(new Set())

  // Resource State
  const [expandedResources, setExpandedResources] = useState<Set<string>>(new Set())
  const [exploredResources, setExploredResources] = useState<Set<string>>(new Set())

  // Achievement State
  const [achievementUnlocked, setAchievementUnlocked] = useState(false)
  const [showAchievement, setShowAchievement] = useState(false)
  const [shareMessage, setShareMessage] = useState('')

  // Knowledge Map State
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  const toggleStage = (index: number) => {
    if (journeyProgress.includes(index)) {
      setJourneyProgress(journeyProgress.filter(i => i !== index))
    } else {
      setJourneyProgress([...journeyProgress, index])
    }
  }

  const activePractice = selectedPractice
    ? spiritualPractices.find(p => p.id === selectedPractice)
    : null

  // Quiz Handlers
  const handleAnswerSelect = (answerIndex: number) => {
    if (showExplanation) return
    setSelectedAnswer(answerIndex)
  }

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return
    setShowExplanation(true)
    setAnsweredQuestions(prev => new Set(prev).add(currentQuestion))
    if (selectedAnswer === quizQuestions[currentQuestion].correctIndex) {
      setCorrectAnswers(prev => prev + 1)
    }
  }

  const handleNextQuestion = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1)
      setSelectedAnswer(null)
      setShowExplanation(false)
    } else {
      setQuizCompleted(true)
    }
  }

  const resetQuiz = () => {
    setCurrentQuestion(0)
    setSelectedAnswer(null)
    setShowExplanation(false)
    setQuizCompleted(false)
    setCorrectAnswers(0)
    setAnsweredQuestions(new Set())
  }

  // Scenario Handlers
  const handleScenarioSelect = (scenarioId: number) => {
    setActiveScenario(activeScenario === scenarioId ? null : scenarioId)
  }

  const handleScenarioAnswer = (scenarioId: number, optionIndex: number) => {
    setScenarioAnswers(prev => ({ ...prev, [scenarioId]: optionIndex }))
    setCompletedScenarios(prev => new Set(prev).add(scenarioId))
  }

  // Resource Handlers
  const toggleResource = (resourceId: string) => {
    setExpandedResources(prev => {
      const newSet = new Set(prev)
      if (newSet.has(resourceId)) {
        newSet.delete(resourceId)
      } else {
        newSet.add(resourceId)
        setExploredResources(explored => new Set(explored).add(resourceId))
      }
      return newSet
    })
  }

  // Achievement Check
  useEffect(() => {
    const quizDone = quizCompleted
    const scenariosDone = completedScenarios.size >= 2
    const resourcesDone = exploredResources.size >= 3

    if (quizDone && scenariosDone && resourcesDone && !achievementUnlocked) {
      setAchievementUnlocked(true)
      setTimeout(() => setShowAchievement(true), 500)
    }
  }, [quizCompleted, completedScenarios.size, exploredResources.size, achievementUnlocked])

  // Share Achievement
  const handleShare = useCallback(() => {
    const score = Math.round((correctAnswers / quizQuestions.length) * 100)
    const message = `I completed the Sacred Wisdom Journey at Godly Library! Scored ${score}% on the quiz, explored ${exploredResources.size} wisdom resources, and practiced ${completedScenarios.size} interfaith scenarios. Discover divine wisdom at godlylibrary.com`
    navigator.clipboard.writeText(message)
    setShareMessage('Copied to clipboard!')
    setTimeout(() => setShareMessage(''), 2000)
  }, [correctAnswers, exploredResources.size, completedScenarios.size])

  return (
    <div className="space-y-12">
      {/* Introduction */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-3xl mx-auto"
      >
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 mb-6 shadow-divine animate-divine-pulse">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-gold-gradient mb-6">
          The Divine Journey
        </h1>
        <p className="text-lg text-gold-700/80 dark:text-gold-300/80 leading-relaxed">
          Your spiritual path is unique, yet follows universal patterns of growth.
          Chart your journey and discover practices that nurture your soul.
        </p>
      </motion.div>

      {/* Section 1: Interactive Summary Quiz */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="sacred-card p-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-gold-100 dark:bg-gold-900/30 flex items-center justify-center">
            <Brain className="w-6 h-6 text-gold-600 dark:text-gold-400" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-gold-800 dark:text-gold-200">
              Sacred Wisdom Quiz
            </h2>
            <p className="text-gold-600/70 dark:text-gold-400/70">
              Test your understanding of world faith traditions
            </p>
          </div>
        </div>

        {!quizCompleted ? (
          <div className="space-y-6">
            {/* Progress Bar */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-2 bg-gold-100 dark:bg-gold-900/30 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gold-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentQuestion + (showExplanation ? 1 : 0)) / quizQuestions.length) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <span className="text-sm text-gold-600 dark:text-gold-400 font-medium">
                {currentQuestion + 1}/{quizQuestions.length}
              </span>
            </div>

            {/* Question */}
            <div className="p-6 bg-gold-50 dark:bg-gold-900/20 rounded-xl">
              <h3 className="text-lg font-semibold text-gold-800 dark:text-gold-200 mb-4">
                {quizQuestions[currentQuestion].question}
              </h3>

              <div className="space-y-3">
                {quizQuestions[currentQuestion].options.map((option, index) => {
                  const isSelected = selectedAnswer === index
                  const isCorrect = index === quizQuestions[currentQuestion].correctIndex
                  const showResult = showExplanation

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(index)}
                      disabled={showExplanation}
                      className={cn(
                        'w-full p-4 rounded-lg text-left transition-all duration-300',
                        !showResult && isSelected && 'bg-gold-200 dark:bg-gold-800/50 border-2 border-gold-500',
                        !showResult && !isSelected && 'bg-white dark:bg-gold-900/30 border-2 border-transparent hover:border-gold-300 dark:hover:border-gold-600',
                        showResult && isCorrect && 'bg-green-100 dark:bg-green-900/30 border-2 border-green-500',
                        showResult && isSelected && !isCorrect && 'bg-red-100 dark:bg-red-900/30 border-2 border-red-500',
                        showResult && !isSelected && !isCorrect && 'bg-white dark:bg-gold-900/30 border-2 border-transparent opacity-50'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <span className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300',
                          !showResult && 'bg-gold-100 dark:bg-gold-800 text-gold-700 dark:text-gold-300',
                          showResult && isCorrect && 'bg-green-500 text-white',
                          showResult && isSelected && !isCorrect && 'bg-red-500 text-white'
                        )}>
                          {showResult && isCorrect ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : showResult && isSelected && !isCorrect ? (
                            <XCircle className="w-5 h-5" />
                          ) : (
                            String.fromCharCode(65 + index)
                          )}
                        </span>
                        <span className={cn(
                          'flex-1 transition-all duration-300',
                          showResult && isCorrect && 'text-green-800 dark:text-green-200 font-medium',
                          showResult && isSelected && !isCorrect && 'text-red-800 dark:text-red-200',
                          !showResult && 'text-gold-800 dark:text-gold-200'
                        )}>
                          {option}
                        </span>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Explanation */}
            <AnimatePresence>
              {showExplanation && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="p-4 bg-gold-100 dark:bg-gold-900/30 rounded-lg border-l-4 border-gold-500"
                >
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-gold-600 dark:text-gold-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gold-700 dark:text-gold-300">
                      {quizQuestions[currentQuestion].explanation}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              {!showExplanation ? (
                <button
                  onClick={handleSubmitAnswer}
                  disabled={selectedAnswer === null}
                  className={cn(
                    'px-6 py-3 rounded-lg font-medium transition-all duration-300',
                    selectedAnswer !== null
                      ? 'bg-gold-500 text-white hover:bg-gold-600'
                      : 'bg-gold-200 dark:bg-gold-800/50 text-gold-500 cursor-not-allowed'
                  )}
                >
                  Submit Answer
                </button>
              ) : (
                <button
                  onClick={handleNextQuestion}
                  className="px-6 py-3 bg-gold-500 text-white rounded-lg font-medium hover:bg-gold-600 transition-all duration-300"
                >
                  {currentQuestion < quizQuestions.length - 1 ? 'Next Question' : 'See Results'}
                </button>
              )}
            </div>
          </div>
        ) : (
          /* Quiz Results */
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="w-24 h-24 rounded-full bg-gold-100 dark:bg-gold-900/30 flex items-center justify-center mx-auto mb-6">
              <Trophy className="w-12 h-12 text-gold-500" />
            </div>
            <h3 className="text-2xl font-bold text-gold-800 dark:text-gold-200 mb-2">
              Quiz Complete!
            </h3>
            <p className="text-4xl font-bold text-gold-600 dark:text-gold-400 mb-4">
              {correctAnswers}/{quizQuestions.length} Correct
            </p>
            <p className="text-gold-600/70 dark:text-gold-400/70 mb-6">
              {correctAnswers === quizQuestions.length
                ? 'Perfect! You have mastered sacred wisdom concepts.'
                : correctAnswers >= 3
                ? 'Well done! You understand interfaith wisdom well.'
                : 'Keep exploring - the journey deepens understanding.'}
            </p>
            <button
              onClick={resetQuiz}
              className="px-6 py-3 bg-gold-100 dark:bg-gold-900/30 text-gold-700 dark:text-gold-300 rounded-lg font-medium hover:bg-gold-200 dark:hover:bg-gold-800/50 transition-all duration-300"
            >
              Retake Quiz
            </button>
          </motion.div>
        )}
      </motion.section>

      {/* Section 2: Knowledge Map (Card-Based) */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="sacred-card p-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-gold-100 dark:bg-gold-900/30 flex items-center justify-center">
            <Globe className="w-6 h-6 text-gold-600 dark:text-gold-400" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-gold-800 dark:text-gold-200">
              Sacred Wisdom Knowledge Map
            </h2>
            <p className="text-gold-600/70 dark:text-gold-400/70">
              Select a category to explore how traditions and practices connect
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {knowledgeCategories.map((category, index) => (
            <motion.button
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
              className={cn(
                'p-6 rounded-xl text-left transition-all duration-300',
                selectedCategory === category.id
                  ? 'shadow-divine scale-105'
                  : 'sacred-card hover:shadow-sacred-lg hover:scale-105'
              )}
              style={{
                backgroundColor: selectedCategory === category.id ? category.color : undefined
              }}
            >
              <div
                className={cn(
                  'w-12 h-12 rounded-full flex items-center justify-center mb-4 transition-all duration-300'
                )}
                style={{
                  backgroundColor: selectedCategory === category.id ? 'rgba(255,255,255,0.2)' : `${category.color}30`,
                  borderColor: category.color,
                  borderWidth: '2px'
                }}
              >
                <Sparkles
                  className={cn(
                    'w-6 h-6 transition-all duration-300',
                    selectedCategory === category.id ? 'text-white' : 'text-gold-600'
                  )}
                />
              </div>
              <h3
                className={cn(
                  'font-display font-semibold mb-2 transition-all duration-300',
                  selectedCategory === category.id
                    ? 'text-white'
                    : 'text-gold-800 dark:text-gold-200'
                )}
              >
                {category.label}
              </h3>
              <p
                className={cn(
                  'text-sm transition-all duration-300',
                  selectedCategory === category.id
                    ? 'text-white/80'
                    : 'text-gold-600/70 dark:text-gold-400/70'
                )}
              >
                {category.description}
              </p>
            </motion.button>
          ))}
        </div>

        <AnimatePresence>
          {selectedCategory && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="mt-6 p-6 bg-gold-50 dark:bg-gold-900/20 rounded-xl"
            >
              <h4 className="font-semibold text-gold-800 dark:text-gold-200 mb-4">
                {knowledgeCategories.find(c => c.id === selectedCategory)?.label} - Connected Elements
              </h4>
              <div className="flex flex-wrap gap-3">
                {knowledgeCategories
                  .find(c => c.id === selectedCategory)
                  ?.items.map((item, index) => (
                    <motion.span
                      key={item}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="px-4 py-2 rounded-full bg-gold-100 dark:bg-gold-900/30 text-gold-700 dark:text-gold-300 text-sm font-medium transition-all duration-300 hover:bg-gold-200 dark:hover:bg-gold-800/50"
                    >
                      {item}
                    </motion.span>
                  ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Legend */}
        <div className="mt-6 flex flex-wrap gap-4 justify-center">
          {knowledgeCategories.map(item => (
            <div key={item.label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full transition-all duration-300"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-sm text-gold-600 dark:text-gold-400">{item.label}</span>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Section 3: Practice Scenarios */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="sacred-card p-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-gold-100 dark:bg-gold-900/30 flex items-center justify-center">
            <Heart className="w-6 h-6 text-gold-600 dark:text-gold-400" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-gold-800 dark:text-gold-200">
              Practice Scenarios
            </h2>
            <p className="text-gold-600/70 dark:text-gold-400/70">
              Apply sacred wisdom to real-world situations ({completedScenarios.size}/{practiceScenarios.length} completed)
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {practiceScenarios.map((scenario) => {
            const isActive = activeScenario === scenario.id
            const isCompleted = completedScenarios.has(scenario.id)
            const selectedOption = scenarioAnswers[scenario.id]

            return (
              <div
                key={scenario.id}
                className={cn(
                  'border-2 rounded-xl overflow-hidden transition-all duration-300',
                  isCompleted
                    ? 'border-gold-500 bg-gold-50/50 dark:bg-gold-900/20'
                    : 'border-gold-200 dark:border-gold-800'
                )}
              >
                <button
                  onClick={() => handleScenarioSelect(scenario.id)}
                  className="w-full p-4 flex items-center justify-between text-left transition-all duration-300"
                >
                  <div className="flex items-center gap-3">
                    {isCompleted ? (
                      <CheckCircle className="w-6 h-6 text-gold-500" />
                    ) : (
                      <div className="w-6 h-6 rounded-full border-2 border-gold-300 dark:border-gold-600 transition-all duration-300" />
                    )}
                    <div>
                      <h3 className="font-semibold text-gold-800 dark:text-gold-200">
                        {scenario.title}
                      </h3>
                      <p className="text-sm text-gold-600/70 dark:text-gold-400/70">
                        {scenario.description.substring(0, 60)}...
                      </p>
                    </div>
                  </div>
                  {isActive ? (
                    <ChevronUp className="w-5 h-5 text-gold-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gold-400" />
                  )}
                </button>

                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-4 pb-4"
                    >
                      <p className="text-gold-700 dark:text-gold-300 mb-4 pl-9">
                        {scenario.description}
                      </p>

                      <div className="space-y-3 pl-9">
                        {scenario.options.map((option, index) => {
                          const isSelected = selectedOption === index

                          return (
                            <button
                              key={index}
                              onClick={() => handleScenarioAnswer(scenario.id, index)}
                              disabled={isCompleted}
                              className={cn(
                                'w-full p-4 rounded-lg text-left transition-all duration-300',
                                isSelected
                                  ? 'bg-gold-200 dark:bg-gold-800/50 border-2 border-gold-500'
                                  : 'bg-white dark:bg-gold-900/30 border-2 border-transparent hover:border-gold-300 dark:hover:border-gold-600',
                                isCompleted && !isSelected && 'opacity-50'
                              )}
                            >
                              <p className="text-gold-800 dark:text-gold-200">{option.text}</p>
                              {isSelected && (
                                <motion.p
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  className="mt-2 text-sm text-gold-600 dark:text-gold-400 border-t border-gold-200 dark:border-gold-700 pt-2"
                                >
                                  {option.feedback}
                                </motion.p>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}
        </div>
      </motion.section>

      {/* Section 4: Resource Library */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="sacred-card p-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-gold-100 dark:bg-gold-900/30 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-gold-600 dark:text-gold-400" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-gold-800 dark:text-gold-200">
              Sacred Wisdom Library
            </h2>
            <p className="text-gold-600/70 dark:text-gold-400/70">
              Expand your understanding ({exploredResources.size}/{resourceLibrary.length} topics explored)
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {resourceLibrary.map((resource) => {
            const Icon = resource.icon
            const isExpanded = expandedResources.has(resource.id)
            const isExplored = exploredResources.has(resource.id)

            return (
              <div
                key={resource.id}
                className={cn(
                  'border-2 rounded-xl overflow-hidden transition-all duration-300',
                  isExplored
                    ? 'border-gold-500'
                    : 'border-gold-200 dark:border-gold-800'
                )}
              >
                <button
                  onClick={() => toggleResource(resource.id)}
                  className="w-full p-4 text-left transition-all duration-300"
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300',
                      isExplored
                        ? 'bg-gold-500 text-white'
                        : 'bg-gold-100 dark:bg-gold-900/30 text-gold-600 dark:text-gold-400'
                    )}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gold-800 dark:text-gold-200 flex items-center gap-2">
                        {resource.title}
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </h3>
                      <p className="text-sm text-gold-600/70 dark:text-gold-400/70 truncate">
                        {resource.description}
                      </p>
                    </div>
                  </div>
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-4 pb-4"
                    >
                      <ul className="space-y-2 ml-13">
                        {resource.topics.map((topic, index) => (
                          <li
                            key={index}
                            className="flex items-start gap-2 text-sm text-gold-700 dark:text-gold-300"
                          >
                            <Sparkles className="w-4 h-4 text-gold-400 flex-shrink-0 mt-0.5" />
                            {topic}
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}
        </div>
      </motion.section>

      {/* Journey Stages - Original Content */}
      <div className="space-y-6">
        <h2 className="font-display text-2xl font-semibold text-center text-gold-800 dark:text-gold-200">
          Stages of the Spiritual Journey
        </h2>

        <div className="relative">
          {/* Progress line */}
          <div className="hidden md:block absolute top-12 left-0 right-0 h-1 bg-gold-200 dark:bg-gold-800/30">
            <motion.div
              className="h-full bg-gold-500"
              initial={{ width: 0 }}
              animate={{
                width: `${(journeyProgress.length / journeyStages.length) * 100}%`
              }}
              transition={{ duration: 0.5 }}
            />
          </div>

          {/* Stages */}
          <div className="grid md:grid-cols-5 gap-6">
            {journeyStages.map((stage, index) => {
              const Icon = stage.icon
              const isComplete = journeyProgress.includes(index)

              return (
                <motion.div
                  key={stage.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center relative"
                >
                  <button
                    onClick={() => toggleStage(index)}
                    className={cn(
                      'w-24 h-24 rounded-full mx-auto flex items-center justify-center transition-all duration-300 relative z-10',
                      isComplete
                        ? 'bg-gold-500 text-white shadow-divine'
                        : 'bg-gold-100 dark:bg-gold-900/30 text-gold-600 dark:text-gold-400 hover:bg-gold-200 dark:hover:bg-gold-800/40'
                    )}
                  >
                    {isComplete ? (
                      <Check className="w-10 h-10" />
                    ) : (
                      <Icon className="w-10 h-10" />
                    )}
                  </button>

                  <h3 className="font-display font-semibold text-gold-800 dark:text-gold-200 mt-4 mb-2">
                    {stage.title}
                  </h3>
                  <p className="text-sm text-gold-600/70 dark:text-gold-400/70 mb-3">
                    {stage.description}
                  </p>

                  <div className="text-xs space-y-1">
                    {stage.practices.map((practice) => (
                      <div
                        key={practice}
                        className="text-gold-500 dark:text-gold-400"
                      >
                        {practice}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>

        {/* Progress summary */}
        <div className="text-center text-gold-600 dark:text-gold-400">
          <span className="font-medium">
            {journeyProgress.length} of {journeyStages.length} stages explored
          </span>
          {journeyProgress.length === journeyStages.length && (
            <span className="ml-2 text-gold-500">Journey complete!</span>
          )}
        </div>
      </div>

      {/* Spiritual Practices - Original Content */}
      <div className="space-y-6">
        <h2 className="font-display text-2xl font-semibold text-center text-gold-800 dark:text-gold-200">
          Sacred Practices for Growth
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {spiritualPractices.map((practice, index) => (
            <motion.button
              key={practice.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setSelectedPractice(
                selectedPractice === practice.id ? null : practice.id
              )}
              className={cn(
                'p-4 rounded-xl text-left transition-all duration-300',
                selectedPractice === practice.id
                  ? 'bg-gold-500 text-white shadow-divine'
                  : 'sacred-card hover:shadow-sacred-lg'
              )}
            >
              <h3 className={cn(
                'font-display font-semibold mb-1 transition-all duration-300',
                selectedPractice === practice.id
                  ? 'text-white'
                  : 'text-gold-800 dark:text-gold-200'
              )}>
                {practice.practice}
              </h3>
              <p className={cn(
                'text-sm transition-all duration-300',
                selectedPractice === practice.id
                  ? 'text-white/80'
                  : 'text-gold-600/70 dark:text-gold-400/70'
              )}>
                {practice.description}
              </p>
            </motion.button>
          ))}
        </div>

        {/* Selected Practice Details */}
        {activePractice && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="sacred-card p-8"
          >
            <h3 className="font-display text-2xl font-bold text-gold-800 dark:text-gold-200 mb-4">
              {activePractice.practice}
            </h3>
            <p className="text-gold-700/80 dark:text-gold-300/80 mb-6">
              {activePractice.description}
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-display font-semibold text-gold-700 dark:text-gold-300 mb-3">
                  Benefits
                </h4>
                <ul className="space-y-2">
                  {activePractice.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-center gap-2 text-sm text-gold-600/70 dark:text-gold-400/70">
                      <Sparkles className="w-4 h-4 text-gold-500" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-display font-semibold text-gold-700 dark:text-gold-300 mb-3">
                  Found In
                </h4>
                <div className="flex flex-wrap gap-2">
                  {activePractice.traditions.map((tradition) => (
                    <span
                      key={tradition}
                      className="px-3 py-1 rounded-full text-sm bg-gold-100 dark:bg-gold-900/30 text-gold-700 dark:text-gold-300"
                    >
                      {tradition}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Section 5: Achievement Badge */}
      <AnimatePresence>
        {showAchievement && (
          <motion.section
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="sacred-card p-8 text-center divine-glow-strong"
          >
            <motion.div
              initial={{ rotate: -10, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              className="w-32 h-32 rounded-full bg-gradient-to-br from-gold-400 via-gold-500 to-gold-600 flex items-center justify-center mx-auto mb-6 shadow-lg"
            >
              <Award className="w-16 h-16 text-white" />
            </motion.div>

            <h2 className="font-display text-3xl font-bold text-gold-gradient mb-4">
              Sacred Wisdom Scholar
            </h2>
            <p className="text-gold-700/80 dark:text-gold-300/80 mb-6 max-w-md mx-auto">
              You have completed the Sacred Wisdom Journey! You understand how world faith traditions share universal truths while honoring their unique insights.
            </p>

            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-6">
              <div className="p-4 bg-gold-100 dark:bg-gold-900/30 rounded-xl">
                <div className="text-2xl font-bold text-gold-600 dark:text-gold-400">
                  {correctAnswers}/{quizQuestions.length}
                </div>
                <div className="text-sm text-gold-500">Quiz Score</div>
              </div>
              <div className="p-4 bg-gold-100 dark:bg-gold-900/30 rounded-xl">
                <div className="text-2xl font-bold text-gold-600 dark:text-gold-400">
                  {completedScenarios.size}
                </div>
                <div className="text-sm text-gold-500">Scenarios</div>
              </div>
              <div className="p-4 bg-gold-100 dark:bg-gold-900/30 rounded-xl">
                <div className="text-2xl font-bold text-gold-600 dark:text-gold-400">
                  {exploredResources.size}
                </div>
                <div className="text-sm text-gold-500">Resources</div>
              </div>
            </div>

            <button
              onClick={handleShare}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gold-500 text-white rounded-lg font-medium hover:bg-gold-600 transition-all duration-300"
            >
              <Share2 className="w-5 h-5" />
              Share Achievement
            </button>

            {shareMessage && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 text-green-600 dark:text-green-400"
              >
                {shareMessage}
              </motion.p>
            )}
          </motion.section>
        )}
      </AnimatePresence>

      {/* Section 6: Continue Learning */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="space-y-6"
      >
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold text-gold-800 dark:text-gold-200 mb-2">
            Continue Your Sacred Journey
          </h2>
          <p className="text-gold-600/70 dark:text-gold-400/70">
            Explore related wisdom in our knowledge network
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {relatedTopics.map((topic) => {
            const Icon = topic.icon

            return (
              <a
                key={topic.title}
                href={topic.url}
                target="_blank"
                rel="noopener noreferrer"
                className="sacred-card p-6 hover:shadow-sacred-lg transition-all duration-300 group"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-gold-100 dark:bg-gold-900/30 flex items-center justify-center group-hover:bg-gold-500 transition-all duration-300">
                    <Icon className="w-5 h-5 text-gold-600 dark:text-gold-400 group-hover:text-white transition-all duration-300" />
                  </div>
                  <ExternalLink className="w-4 h-4 text-gold-400 opacity-0 group-hover:opacity-100 transition-all duration-300" />
                </div>
                <h3 className="font-display font-semibold text-gold-800 dark:text-gold-200 mb-1">
                  {topic.title}
                </h3>
                <p className="text-sm text-gold-600/70 dark:text-gold-400/70">
                  {topic.description}
                </p>
              </a>
            )
          })}
        </div>
      </motion.section>

      {/* Closing Wisdom - Original Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="sacred-card p-10 text-center divine-glow-strong ornamental max-w-3xl mx-auto"
      >
        <Sparkles className="w-12 h-12 text-gold-500 mx-auto mb-6" />
        <blockquote className="font-serif text-2xl italic text-gold-800 dark:text-gold-200 mb-6">
          "{inspirationalQuotes[4].text}"
        </blockquote>
        <footer className="text-gold-600 dark:text-gold-400">
          - {inspirationalQuotes[4].source}, {inspirationalQuotes[4].tradition}
        </footer>
      </motion.div>

      {/* Call to Action - Original Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="text-center space-y-6"
      >
        <h2 className="font-display text-3xl font-bold text-gold-gradient">
          Continue Your Journey
        </h2>
        <p className="text-lg text-gold-700/70 dark:text-gold-300/70 max-w-2xl mx-auto">
          The Godly Library is your companion on the path of spiritual discovery.
          May you find the wisdom you seek and the peace your soul longs for.
        </p>
        <div className="flex flex-wrap justify-center gap-4 pt-4">
          <div className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gold-100 dark:bg-gold-900/30 text-gold-700 dark:text-gold-300 transition-all duration-300 hover:bg-gold-200 dark:hover:bg-gold-800/50">
            <BookOpen className="w-5 h-5" />
            <span>Explore Texts</span>
          </div>
          <div className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gold-100 dark:bg-gold-900/30 text-gold-700 dark:text-gold-300 transition-all duration-300 hover:bg-gold-200 dark:hover:bg-gold-800/50">
            <Users className="w-5 h-5" />
            <span>Join Community</span>
          </div>
          <div className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gold-100 dark:bg-gold-900/30 text-gold-700 dark:text-gold-300 transition-all duration-300 hover:bg-gold-200 dark:hover:bg-gold-800/50">
            <Heart className="w-5 h-5" />
            <span>Daily Wisdom</span>
          </div>
        </div>
      </motion.div>

      {/* Blessing - Original Content */}
      <div className="text-center py-8">
        <div className="flex justify-center gap-3 text-3xl mb-4">
          + * * * * *
        </div>
        <p className="font-serif text-xl italic text-gold-600 dark:text-gold-400">
          "May peace be upon all who seek truth with sincere hearts."
        </p>
      </div>
    </div>
  )
}
